import { createFileRoute } from "@tanstack/react-router";
import { z } from "zod";
import { Container, Typography, Box, CircularProgress } from "@mui/material";
import PublicProjectForm from "@/components/PublicProjectForm";
import { fetchProjectPublic, submitPublicForm } from "@/api/projectsApi";
import { useQuery } from "@tanstack/react-query";
import { ApiError } from "@/types/Api";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";

const searchSchema = z.object({
  video_id: z.string().uuid().optional(),
});

export const Route = createFileRoute("/public/$projectId")({
  validateSearch: searchSchema,
  component: RouteComponent,
});

function ComingSoonPage() {
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "100vh",
        background:
          "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
      }}
    >
      <Container maxWidth="sm">
        <Box
          sx={{
            textAlign: "center",
            py: 8,
          }}
        >
          <RocketLaunchIcon
            sx={{ fontSize: 64, color: "#6366f1", mb: 3 }}
          />
          <Typography
            variant="h3"
            sx={{
              fontWeight: 700,
              color: "#1e293b",
              mb: 2,
            }}
          >
            Coming Soon
          </Typography>
          <Typography
            variant="h6"
            sx={{
              color: "#64748b",
              fontWeight: 400,
              lineHeight: 1.6,
            }}
          >
            This project is being prepared and will be available shortly.
            <br />
            Please check back later.
          </Typography>
        </Box>
      </Container>
    </Box>
  );
}

function RouteComponent() {
  const { projectId } = Route.useParams();
  const { video_id: videoId } = Route.useSearch();

  const {
    data: project,
    isLoading: projectLoading,
    error: projectError,
  } = useQuery({
    queryKey: ["publicProject", projectId],
    queryFn: () => fetchProjectPublic(projectId as string),
  });

  const handleSubmit = async (formData: Record<string, string>) => {
    if (!projectId) return;
    await submitPublicForm(projectId, formData, videoId);
  };

  if (projectLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "80vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // Draft project — show coming soon page
  if (
    projectError instanceof ApiError &&
    projectError.status === 404
  ) {
    return <ComingSoonPage />;
  }

  // Expired/archived/unavailable project
  if (
    projectError instanceof ApiError &&
    projectError.status === 410
  ) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
          background:
            "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
        }}
      >
        <Container maxWidth="sm">
          <Box sx={{ textAlign: "center", py: 8 }}>
            <Typography
              variant="h4"
              sx={{ fontWeight: 700, color: "#1e293b", mb: 2 }}
            >
              This event has ended
            </Typography>
            <Typography
              variant="h6"
              sx={{ color: "#64748b", fontWeight: 400 }}
            >
              This project is no longer available.
            </Typography>
          </Box>
        </Container>
      </Box>
    );
  }

  if (projectError || !project) {
    return (
      <Container maxWidth="md">
        <Typography variant="h5" color="error" align="center">
          {projectError instanceof Error
            ? projectError.message
            : "Project not found"}
        </Typography>
      </Container>
    );
  }

  return <PublicProjectForm project={project} onSubmit={handleSubmit} />;
}
